import {getFeatureReference} from '@apps-common/geocorn-sdk/api/util';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import copy from 'copy-to-clipboard';
import {bool, object} from 'prop-types';
import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ActionsMenuButton from '../../common/ActionsMenuButton.jsx';
import MenuItem from '../../common/MenuItem.jsx';
import DeleteFeatureModal from './DeleteFeatureModal.jsx';
import JumpToExplorer from './JumpToExplorer.jsx';
import {notify} from '@actions/notifications';
import {deselectFeatureRef, selectFeatureRef} from '@actions/selected-features';
import {setTourIndex} from '@app/actions/tour';
import {trackEvent} from '@app/analytics';
import {TOUR_STAGES} from '@app/constants';
import {refEquals} from '@app/util/granero';

const MODALS = {
  EDIT: 'edit',
  DELETE: 'delete',
};

const EVENT_NAME = 'Feature Actions Menu';

const MoreActionsMenu = ({feature, isOwner, showViewDetails = true}) => {
  const dispatch = useDispatch();

  const selectedFeatureRef = useSelector(state => state.selectedFeatureRef);
  const featureRef = getFeatureReference(feature);
  const isFeatureSelected = refEquals(selectedFeatureRef, featureRef);
  const isTourRunning = useSelector(state => state.tour.run);

  const [modal, setModal] = useState('');

  const closeModal = useCallback(() => {
    setModal('');
  }, [setModal]);

  const onClick = () => {
    trackEvent(EVENT_NAME, {
      action: 'opened',
      featureId: feature.id,
    });
    if (isTourRunning) {
      dispatch(setTourIndex(1));
    }
  };

  const onCopyReference = () => {
    copy(featureRef);
    dispatch(
      notify({
        message: 'Copied feature reference URI!',
      })
    );
    trackEvent(EVENT_NAME, {
      action: 'Copy reference',
      featureRef,
    });
  };

  const handleFeatureSelect = feature => {
    if (isFeatureSelected) {
      dispatch(deselectFeatureRef());
    } else {
      dispatch(selectFeatureRef(getFeatureReference(feature)));
    }
  };

  return (
    <>
      <ActionsMenuButton
        tooltipTitle="More"
        onClick={onClick}
        data-tour={TOUR_STAGES.FEATURE_MORE_MENU_BUTTON}
        MenuProps={{
          MenuListProps: {
            'data-tour': TOUR_STAGES.FEATURE_MORE_MENU_LIST,
          },
        }}
      >
        <MenuItem
          label="Copy reference"
          icon={<ContentCopyOutlinedIcon />}
          onClick={onCopyReference}
        />
        {showViewDetails && (
          <MenuItem
            label="View details"
            icon={<ListAltOutlinedIcon />}
            onClick={() => {
              handleFeatureSelect(feature);
            }}
          />
        )}

        <JumpToExplorer feature={feature} divider />

        <MenuItem
          label="Delete"
          icon={<DeleteOutlinedIcon />}
          color="error"
          onClick={() => {
            trackEvent(EVENT_NAME, {
              action: 'Opened delete modal',
              feature,
            });
            setModal(MODALS.DELETE);
          }}
          disabled={!isOwner || feature?.locked}
          tooltipText={
            feature?.locked ? 'Cannot be deleted because quota is in use' : ''
          }
        />
      </ActionsMenuButton>
      <DeleteFeatureModal
        open={modal === MODALS.DELETE}
        feature={feature}
        onClose={closeModal}
      />
    </>
  );
};

MoreActionsMenu.propTypes = {
  feature: object,
  isOwner: bool,
  showViewDetails: bool,
};

export default MoreActionsMenu;
